import {
  Mail,
  Eye,
  Building2,
  Phone,
  Briefcase,
  Euro,
  User,
  MapPin,
  Calendar
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function ProspectProfile({ prospect, onViewDetails }) {
  const isPM = prospect?.type === "Personne Morale";

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
  };

  return (
    <div className="flex flex-col justify-between workspace-card bg-surface shadow-hp-sm rounded-xl p-6 h-[300px] flex-[1.5] xl:flex-[2] min-w-[350px] relative overflow-hidden">
      {!prospect ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4">
          <img src={'/assets/logo-hipparchus.png'} alt="Logo Nanthiat" className="w-16 h-16 opacity-40 grayscale" />
          <p className="text-subtle font-medium">Sélectionnez un prospect pour voir ses informations.</p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={prospect._id || prospect.id}
            variants={containerVariants}
            initial="hidden"
            animate="show"
            exit="hidden"
            className="flex flex-col h-full justify-start gap-6"
          >
            {/* Header Profil */}
            <div className="flex items-start gap-5">
              <motion.div variants={itemVariants} className="w-16 h-16 shrink-0 flex items-center justify-center bg-primary-50 rounded-full text-primary-600 shadow-sm border border-primary-100">
                {isPM ? <Building2 className="w-8 h-8" /> : <User className="w-8 h-8" />}
              </motion.div>

              <div className="flex-1 min-w-0 mt-1 flex justify-between items-start gap-4">
                <div>
                  <motion.h3 variants={itemVariants} className="text-xl font-bold text-primary truncate">
                    {isPM ? prospect.organizationName : `${prospect.firstName || ""} ${prospect.lastName || ""}`.trim()}
                  </motion.h3>
                  <motion.div variants={itemVariants} className="flex items-center gap-2 mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-surface-muted text-subtle border border-workspace uppercase tracking-wide">
                      {prospect.type}
                    </span>
                    {isPM && prospect.siret && (
                       <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-surface-muted text-subtle border border-workspace">
                        SIRET: {prospect.siret}
                      </span>
                    )}
                  </motion.div>
                </div>
                
                <motion.div variants={itemVariants}>
                  <button
                    className="btn btn-secondary py-1.5 px-3 text-sm h-8"
                    onClick={() => onViewDetails && onViewDetails(prospect._id || prospect.id)}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                    Dossier
                  </button>
                </motion.div>
              </div>
            </div>

            {/* Infos sous forme de grille aérée */}
            <motion.div variants={containerVariants} className="grid grid-cols-2 gap-x-4 gap-y-4">
              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <div className="p-2 bg-surface-muted rounded-lg text-subtle border border-workspace"><Briefcase className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">Profession / Secteur</p>
                  <p className="text-sm text-primary font-medium truncate">{isPM ? (prospect.sector || "-") : (prospect.position || "-")}</p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <div className="p-2 bg-surface-muted rounded-lg text-subtle border border-workspace"><Phone className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">Téléphone</p>
                  <p className="text-sm text-primary font-medium truncate">{prospect.phone || "-"}</p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <div className="p-2 bg-surface-muted rounded-lg text-subtle border border-workspace"><Mail className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">Email</p>
                  <p className="text-sm text-primary font-medium truncate">{prospect.email || "-"}</p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <div className="p-2 bg-surface-muted rounded-lg text-subtle border border-workspace"><Euro className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">Enveloppe estimée</p>
                  <p className="text-sm text-primary font-medium truncate">
                    {prospect.estimatedWealthBracket || "-"}
                  </p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <div className="p-2 bg-surface-muted rounded-lg text-subtle border border-workspace"><MapPin className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">Localisation</p>
                  <p className="text-sm text-primary font-medium truncate">
                    {[prospect.city, prospect.country].filter(Boolean).join(", ") || "-"}
                  </p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center gap-3">
                <div className="p-2 bg-surface-muted rounded-lg text-subtle border border-workspace"><Calendar className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">1er Contact</p>
                  <p className="text-sm text-primary font-medium truncate">
                    {prospect.createdAt ? new Date(prospect.createdAt).toLocaleDateString('fr-FR') : "-"}
                  </p>
                </div>
              </motion.div>
            </motion.div>

          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
